/**
 * The collision pass, wrapping `ramming.ts` for the roster tick the way
 * `bomb-pass.ts` wraps `bombs.ts`: the geometry module stays pure and knows
 * nothing about `CombatAircraft`, and this file does the reading-out and the
 * turning of a finding into damage.
 *
 * ## Where in the tick
 *
 * After **fly**, against the *stepped* poses — unlike gunnery, which resolves
 * against the pre-step picture because a pilot aims at what he can see. Nobody
 * aims a collision. `intel\move.c` moves an object and then asks whether it hit
 * anything, and that is the physically honest order: two aeroplanes have either
 * ended the tick inside each other or they have not.
 *
 * ## What a collision does
 *
 * `collide.c` calls `damage.c` with **kind 6**, which that module special-cases
 * ahead of its severity table as outright destruction — it never reaches the
 * accumulator, it flags the player out, and it seeds the wreck with two random
 * tumble rates (AD-017 §4). This port expresses "the airframe is finished" the
 * way it already does everywhere else: both wing cells taken out, which raises
 * `wingsFailed`, which `aircraftStatus` reads as `outOfControl`, which is
 * AD-014's death arc. The wreck then tumbles down and is destroyed on arrival —
 * the same shape the original's random tumble describes, reached through
 * machinery that already exists rather than a second way to die.
 *
 * Routing it through `applyHits` as ordinary hits buys three more things for
 * free: AIRCRAFT MAY BE DAMAGED is honoured (that gate lives in `applyHits`),
 * `lastAttackerId` names the other party so kill credit and the witness rules
 * work unchanged, and the `takingHits` event feed carries it like any other blow.
 *
 * A gasbag is not wounded but **ignited**. The original puts the ram on a
 * separate ladder — `ds:0x1046[6] = 500` against an ignition threshold of 200 —
 * so a gasbag that is flown into catches fire on the spot, every time, with no
 * roll. That is a fire, not a deletion: it burns for its profile's `burnSeconds`
 * and sinks, which is exactly what `advanceTarget` already does for one shot
 * down with incendiary.
 */

import { subsystemEnabled } from "../flight/tiers.ts";
import type { Tunables } from "../flight/tunables.ts";
import type { CombatAircraft } from "./encounter.ts";
import { bodyOf } from "./geometry.ts";
import type { RoundHit } from "./hit-allocation.ts";
import {
  airframeCollisionRadiusM,
  detectCollisions,
  gasbagHullM,
  type RamAircraft,
  type RamCollision,
  type RamGasbag,
} from "./ramming.ts";
import { targetBody, type EncounterTarget } from "./targets.ts";
import type { SimState } from "../flight/state.ts";

/** One aeroplane's pre-step record paired with the state its own `step` produced. */
export interface RamStep {
  readonly entry: CombatAircraft;
  readonly sim: SimState;
}

export interface RamResult {
  readonly collisions: readonly RamCollision[];
  /** Airframe damage, shaped exactly like gunfire so `settle` can apply it unchanged. */
  readonly hits: readonly RoundHit[];
  /** Gasbag id → the pilot who flew into it. */
  readonly ignitions: ReadonlyMap<string, string>;
}

const NO_COLLISIONS: RamResult = { collisions: [], hits: [], ignitions: new Map() };

/**
 * Both realism gates, in the original's own order: AIRCRAFT MAY BE DAMAGED is
 * tested at `0x1B084` and MIDAIR COLLISIONS at `0x1B08E`, both above the
 * object-kind switch, so neither an aeroplane nor a Zeppelin is solid without
 * the pair of them.
 */
function collisionsEnabled(sim: SimState): boolean {
  if (!sim.config.realism.aircraftMayBeDamaged) return false;
  return subsystemEnabled("midairCollisions", sim.config.tier, sim.config.realism);
}

function ramAircraftOf(stepped: RamStep): RamAircraft {
  return {
    id: stepped.entry.id,
    body: bodyOf(stepped.sim),
    radiusM: airframeCollisionRadiusM(stepped.sim.config.spec.structure.collisionSizeRaw),
    human: stepped.entry.ai === null,
    collisionsEnabled: collisionsEnabled(stepped.sim),
    // A wreck still falling is solid — it is 700 kg of airframe in the middle of
    // the fight. One that has already arrived has left the roster's geometry.
    present: stepped.entry.status !== "destroyed",
  };
}

function ramGasbagOf(target: EncounterTarget, tunables: Tunables): RamGasbag | null {
  const hull = gasbagHullM(target.kind, tunables);
  if (!hull) return null;
  return { id: target.id, body: targetBody(target), hull, present: !target.destroyed };
}

/**
 * The blow itself: both wing cells, which is this port's own spelling of "this
 * airframe is finished". `rangeM` is zero because a collision happens at
 * contact, and the aspect is the face that struck.
 */
function airframeHits(victimId: string, attackerId: string, collision: RamCollision): RoundHit[] {
  return [
    { shooterId: attackerId, targetId: victimId, component: "wingLeft", amount: 1, rangeM: 0, aspect: collision.aspect },
    { shooterId: attackerId, targetId: victimId, component: "wingRight", amount: 1, rangeM: 0, aspect: collision.aspect },
  ];
}

/** One tick of collision for the whole roster. */
export function ramPass(
  flown: readonly RamStep[],
  targets: readonly EncounterTarget[],
  tunables: Tunables,
): RamResult {
  const aircraft = flown.map(ramAircraftOf);
  // Nothing to do at all unless somebody's panel has the toggle on — which is
  // the common case, since MISSION_REALISM leaves it off.
  if (!aircraft.some((entry) => entry.collisionsEnabled)) return NO_COLLISIONS;

  const gasbags: RamGasbag[] = [];
  for (const target of targets) {
    const gasbag = ramGasbagOf(target, tunables);
    if (gasbag) gasbags.push(gasbag);
  }

  const collisions = detectCollisions({ aircraft, gasbags });
  if (collisions.length === 0) return NO_COLLISIONS;

  const hits: RoundHit[] = [];
  const ignitions = new Map<string, string>();
  for (const collision of collisions) {
    hits.push(...airframeHits(collision.aircraftId, collision.otherId, collision));
    if (collision.otherIsAircraft) {
      // Both die. `collide.c` calls `damage(other, 6)` and `damage(self, 6)` —
      // there is no glancing blow and no survivor.
      hits.push(...airframeHits(collision.otherId, collision.aircraftId, collision));
    } else if (!ignitions.has(collision.otherId)) {
      ignitions.set(collision.otherId, collision.aircraftId);
    }
  }
  return { collisions, hits, ignitions };
}
